// Development floor and ceiling calculation utilities
import { ChildProfile, DomainKey } from '../types/profile';
import { MilestoneWithText } from '../types/milestones';
import { appConfig } from '../config/configManager';
import { AdminSegmentStorage } from '../services/AdminSegmentStorage';
import { StorageInterface } from '../stores/childNavStores';

export interface DevFloorCeilingResult {
  floor: number;
  ceiling: number;
  devstate: number;
  updated: boolean;
  showProgress?: boolean; // Indicates UI should show progress indicator
}

/**
 * Calculate and set development floor and ceiling for a domain based on physical age and milestone states
 * 
 * @param profile - The child profile to update
 * @param domain - The development domain key
 * @param domainMilestones - All milestones for the domain, sorted by seqnum
 * @param storage - Storage interface for accessing AdminNodeDto data (optional)
 * @returns Object with floor, ceiling values and whether profile was updated
 */
export const calculateDevFloorCeiling = async (
  profile: ChildProfile,
  domain: DomainKey,
  domainMilestones: MilestoneWithText[],
  storage?: StorageInterface
): Promise<DevFloorCeilingResult> => {
  console.log('🔧 calculateDevFloorCeiling: Starting calculation for domain:', domain);
  console.log('   Profile ID:', profile.id, 'Physical Age:', profile.agephysical.toFixed(1) + 'm');
  console.log('   Milestones count:', domainMilestones.length);
  console.log('   Current floor:', profile.devfloor[domain], 'Current ceiling:', profile.devceiling[domain]);
  
  console.log('   Storage available:', !!storage);
  
  const config = appConfig.getConfig();
  const physicalAge = profile.agephysical;
  const devInitWindow = config.development.devInitWindow;
  const devYesRequired = config.development.devYesRequired;
  const devNotYetRequired = config.development.devNotYetRequired;
  
  console.log('   Config values - devInitWindow:', devInitWindow, 'devYesRequired:', devYesRequired, 'devNotYetRequired:', devNotYetRequired);
  
  let currentFloor = profile.devfloor[domain];
  let currentCeiling = profile.devceiling[domain];
  let currentDevState = profile.devstate[domain];
  let updated = false;
  
  // Helper function to get milestone state for a milestone
  const getMilestoneState = (mvar: string): 'yes' | 'sometimes' | 'not-yet' | null => {
    const milestoneState = profile.milestonestate.find(ms => ms.mvarid === mvar);
    return milestoneState?.state || null;
  };
  
  // Load AdminNodeDto once — shared by all devstate branches
  const milestoneByMVar = new Map<string, MilestoneWithText>();
  domainMilestones.forEach(m => milestoneByMVar.set(m.mvar, m));
  let orderedMilestones: Array<{ milestone: MilestoneWithText, orderIndex: number }> = [];

  if (storage) {
    const isPremium = appConfig.isPremium();
    const adminNodeName = mapDomainToAdminNodeName(domain, isPremium);
    try {
      const adminSegmentStorage = new AdminSegmentStorage(storage);
      const adminNode = await adminSegmentStorage.getAdminNodeByDomain(adminNodeName);
      if (adminNode?.Order?.length) {
        console.log(`✅ AdminNodeDto loaded for ${domain}: ${adminNode.Order.length} items`);
        adminNode.Order.forEach((orderItem, index) => {
          const milestone = milestoneByMVar.get(orderItem.Name);
          if (milestone) orderedMilestones.push({ milestone, orderIndex: index + 1 });
        });
      } else {
        console.warn(`⚠️ AdminNodeDto.Order not available for ${domain}, using seqnum ordering`);
        orderedMilestones = domainMilestones.sort((a, b) => a.seqnum - b.seqnum).map(m => ({ milestone: m, orderIndex: m.seqnum }));
      }
    } catch (error) {
      console.error(`❌ Error loading AdminNodeDto for ${domain}:`, error);
      orderedMilestones = domainMilestones.sort((a, b) => a.seqnum - b.seqnum).map(m => ({ milestone: m, orderIndex: m.seqnum }));
    }
  } else {
    console.warn('⚠️ No storage provided, using seqnum ordering');
    orderedMilestones = domainMilestones.sort((a, b) => a.seqnum - b.seqnum).map(m => ({ milestone: m, orderIndex: m.seqnum }));
  }

  // Pre-build index-keyed lookup used by ceiling calculation
  const milestoneMap = new Map<number, MilestoneWithText>();
  orderedMilestones.forEach(item => milestoneMap.set(item.orderIndex, item.milestone));
  console.log('   Ordered milestones loaded:', orderedMilestones.length, 'items');

  // Step 1: Initial calculation when devstate is 0 (compute devInitWindow based on physical age)
  // or when floor/ceiling is blank for backward compatibility
  if (currentDevState === 0 || !currentCeiling || currentCeiling === 0 || !currentFloor || currentFloor === 0) {
    console.log('📊 calculateDevFloorCeiling: Performing INITIAL calculation (devstate=0 or blank floor/ceiling)');
    console.log('   Current devstate:', currentDevState);
    
    if (orderedMilestones.length === 0) {
      console.warn('⚠️ No ordered milestones available for initial calculation');
      return { floor: 1, ceiling: 1, devstate: 0, updated: false, showProgress: false };
    }
    
    // Find the milestone with the greatest itemage that is less than physicalAge
    const eligibleMilestones = orderedMilestones
      .filter(item => item.milestone.itemage < physicalAge)
      .sort((a, b) => b.milestone.itemage - a.milestone.itemage); // Sort by itemage descending
    
    console.log('   Eligible milestones (itemage < physicalAge):', eligibleMilestones.length);
    
    if (eligibleMilestones.length > 0) {
      currentCeiling = eligibleMilestones[0].orderIndex;
      updated = true;
      console.log('   Set ceiling to order index:', currentCeiling, 'with itemage:', eligibleMilestones[0].milestone.itemage);
    } else {
      // If no milestones are below physical age, set ceiling to first milestone
      currentCeiling = orderedMilestones.length > 0 ? orderedMilestones[0].orderIndex : 1;
      updated = true;
      console.log('   No eligible milestones, set ceiling to first milestone:', currentCeiling);
    }
    
    // Set floor to create a range of devInitWindow milestones
    const minOrderIndex = Math.min(...orderedMilestones.map(item => item.orderIndex));
    currentFloor = Math.max(minOrderIndex, currentCeiling - devInitWindow + 1);
    
    // Mark devstate as computed (no longer initial state)
    currentDevState = 1;
    
    updated = true;
    console.log('   Set floor to create', devInitWindow, 'milestone window:', currentFloor);
    console.log('   Set devstate to 1 (computed state)');
  }
  
  // Step 1.5: Check milestone completeness when devstate is 1 (initial window computed)
  else if (currentDevState === 1) {
    console.log('📊 calculateDevFloorCeiling: Checking milestone completeness (devstate=1)');
    console.log('   Current floor:', currentFloor, 'Current ceiling:', currentCeiling);
    
    // Ensure we have valid floor/ceiling values
    if (!currentFloor || currentFloor === 0 || !currentCeiling || currentCeiling === 0) {
      console.warn('⚠️ devstate=1 but missing floor/ceiling - resetting to initial calculation');
      currentDevState = 0;
      // This will trigger the initial calculation on the next function call
      return { 
        floor: currentFloor || 1, 
        ceiling: currentCeiling || 1, 
        devstate: 0, 
        updated: true,
        showProgress: false
      };
    }
    
    // Check if all milestones in the floor-ceiling range have been answered
    // Now we know currentFloor and currentCeiling are not null due to check above
    const milestonesInRange = orderedMilestones.filter(
      item => item.orderIndex >= currentFloor! && item.orderIndex <= currentCeiling!
    );
    
    let allMilestonesAnswered = true;
    let answeredCount = 0;
    
    console.log('   Checking', milestonesInRange.length, 'milestones in range:', currentFloor, 'to', currentCeiling);
    
    for (const item of milestonesInRange) {
      const milestoneState = getMilestoneState(item.milestone.mvar);
      if (milestoneState && milestoneState !== null) {
        answeredCount++;
        console.log('   ✅', item.milestone.mvar, '(order', item.orderIndex, '):', milestoneState);
      } else {
        allMilestonesAnswered = false;
        console.log('   ⭕', item.milestone.mvar, '(order', item.orderIndex, '): not answered');
      }
    }
    
    console.log('   Milestone completion:', answeredCount, '/', milestonesInRange.length);
    
    if (allMilestonesAnswered && milestonesInRange.length > 0) {
      console.log('   ✅ All milestones in window are answered - proceeding to devFloor calculation');
      // Mark as ready for devFloor calculation (don't change ceiling yet)
      currentDevState = 2;
      updated = true;
      console.log('   Set devstate to 2 (devFloor calculation phase) - continuing to devFloor processing');
      // Continue to devstate=2 processing below (don't return here)
    } else {
      console.log('   ⏳ Window incomplete - keeping devstate=1, no floor/ceiling changes');
      // Return current values without changes (except updated flag if devstate changed)
      return { 
        floor: currentFloor, 
        ceiling: currentCeiling, 
        devstate: currentDevState, 
        updated: false,
        showProgress: false
      };
    }
  }
  
  // bypass currentDevState=2 processing for now
  if (currentDevState === 2) { currentDevState = 3; }
  
  // Step 2: DevCeiling calculation when devstate is 3+ (separate from devFloor processing)
  if (currentDevState >= 3) {
    console.log('📊 calculateDevFloorCeiling: Performing DEVCEILING calculation (devstate>=3)');
    console.log('   Current floor:', currentFloor, 'Current ceiling:', currentCeiling);
    console.log('   Focus: First recalculate devFloor from order list, then devCeiling calculation');
    
    // First: Recalculate devFloor from the beginning of the order list in case new answers changed optimal position
    console.log('   🔍 Recalculating devFloor from beginning of order list for devstate=3');

    if (orderedMilestones.length === 0) {
      console.warn('⚠️ No ordered milestones available for devFloor recalculation');
    } else {
      // DevFloor recalculation logic for devstate=3
      const minOrderIndex = Math.min(...orderedMilestones.map(item => item.orderIndex));
      const maxOrderIndex = Math.max(...orderedMilestones.map(item => item.orderIndex));
      let newFloorFromOrder: number | null = null;
      let newCeilingFromOrder: number | null = null;
      newCeilingFromOrder = currentCeiling;
      newFloorFromOrder = currentFloor;
      
      console.log('   📋 Starting devFloor recalculation from position', minOrderIndex);
      
      // Step 1: Find first non-null answer starting from beginning
      let firstWindowIndex: number | null = null;
      let firstWindowState: string | null = null;
      
      for (let orderIndex = minOrderIndex; orderIndex <= maxOrderIndex; orderIndex++) {
        const milestoneAtIndex = orderedMilestones.find(item => item.orderIndex === orderIndex);
        if (milestoneAtIndex) {
          const milestoneState = getMilestoneState(milestoneAtIndex.milestone.mvar);
          if (milestoneState !== null) {
            firstWindowIndex = orderIndex;
            firstWindowState = milestoneState;
            console.log('   📍 First non-null answer found at position', orderIndex, '(', milestoneAtIndex.milestone.mvar, '):', milestoneState);
            break;
          }
        }
      }
      
      if (firstWindowIndex !== null) {
        if (firstWindowState === 'yes') {
          console.log('   ✅ First non-null answer is YES - continuing to find first non-Yes answer');
          
          // Step 2: Continue from first Yes answer to find first non-Yes answer
          for (let orderIndex = firstWindowIndex + 1; orderIndex <= maxOrderIndex; orderIndex++) {
            const milestoneAtIndex = orderedMilestones.find(item => item.orderIndex === orderIndex);
            if (milestoneAtIndex) {
              const milestoneState = getMilestoneState(milestoneAtIndex.milestone.mvar);
              firstWindowIndex = orderIndex;
              if (milestoneState !== 'yes') {
                console.log('   🛑 First non-Yes answer found at position', orderIndex, '(', milestoneAtIndex.milestone.mvar, '):', milestoneState);
                break;
              } else {
                // console.log('   ✅ Continuing Yes answer at position', orderIndex, '(', milestoneAtIndex.milestone.mvar, ')');
              }
            }
          }
        } 
      
        // Step 3: Calculate new devFloor position
        newFloorFromOrder = Math.max(1, firstWindowIndex - devYesRequired);
        console.log('   📊 Calculated new devFloor:', firstWindowIndex, '- devYesRequired(', devYesRequired, ') = position', newFloorFromOrder);
      
        // Step 4: make sure the current ceiling is devInitWindow above the new floor
        if ((newFloorFromOrder + devInitWindow - 1) > newCeilingFromOrder) {
          newCeilingFromOrder = newFloorFromOrder + devInitWindow - 1;
          console.log('   ⚠️ Adjusted devCeiling to newFloorFromOrder + devInitWindow - 1:', newCeilingFromOrder);
        }
       
        // Step 4a: make sure the new ceiling does not exceed max order index
        if (newCeilingFromOrder > maxOrderIndex) {
          newCeilingFromOrder = maxOrderIndex;
          newFloorFromOrder = Math.max(1, newCeilingFromOrder - devInitWindow + 1);
          console.log('   ⚠️ Adjusted devCeiling to max position', maxOrderIndex, 'and recalculated devFloor to', newFloorFromOrder);
        }
        
      } else {
        console.log('   ⏭️ No non-null answers found - no devFloor recalculation possible');
      }
      
      // Step 5: Check if devFloor or devCeiling needs updating
      if (newFloorFromOrder !== currentFloor) {
        console.log('   📉 DevFloor recalculation: changing from', currentFloor, 'to', newFloorFromOrder);
        currentFloor = newFloorFromOrder;
        currentCeiling = newCeilingFromOrder;
        updated = true;
        
        // Show progress indicator for devFloor recalculation
        return {
          floor: currentFloor,
          ceiling: currentCeiling,
          devstate: currentDevState,
          updated: true,
          showProgress: true // Show progress for devFloor recalculation
        };
      } else if (newCeilingFromOrder !== currentCeiling) {
        console.log('   📈 DevCeiling recalculation: changing from', currentCeiling, 'to', newCeilingFromOrder);
        currentFloor = newFloorFromOrder;
        currentCeiling = newCeilingFromOrder;
        updated = true;
        
        // Show progress indicator for devFloor recalculation
        return {
          floor: currentFloor,
          ceiling: currentCeiling,
          devstate: currentDevState,
          updated: true,
          showProgress: false // Show progress for devFloor recalculation
        };
      } else {
        console.log('   ⏭️ no non-null answers found - no devFloor recalculation needed');
      }
    }
    
    // Continue devCeiling calculation
    if (orderedMilestones.length === 0) {
      console.warn('⚠️ No ordered milestones available, keeping current floor/ceiling');
      return { floor: currentFloor, ceiling: currentCeiling, devstate: currentDevState, updated: false, showProgress: false };
    }

    // Get the min and max order indices
    const minOrderIndex = Math.min(...orderedMilestones.map(item => item.orderIndex));
    const maxOrderIndex = Math.max(...orderedMilestones.map(item => item.orderIndex));
    
    // Calculate new ceiling: Start at floor and walk up (devFloor already calculated in devstate=2)
    let newCeiling = currentCeiling;
    const floorMilestone = milestoneMap.get(currentFloor);
    if (floorMilestone) {
      // let foundNonBlank = false;
      let notYetCount = 0;
      let currentOrderIndex = currentFloor;
       
      // Continue counting 'not-yet' or blank states
      while (currentOrderIndex <= maxOrderIndex && notYetCount < devNotYetRequired) {
        const milestone = milestoneMap.get(currentOrderIndex);
        if (milestone) {
          const state = getMilestoneState(milestone.mvar);
          if (state === 'not-yet' || state === null) {
            notYetCount++;
            if (notYetCount === devNotYetRequired) {
              newCeiling = currentOrderIndex;
              break;
            }
          }
        }
        currentOrderIndex++;
      }

      
      // Step 4: make sure the current ceiling is devInitWindow above the new floor
      if ((currentFloor + devInitWindow - 1) > newCeiling) {
        newCeiling = currentFloor + devInitWindow - 1;
        console.log('   ⚠️ Adjusted devCeiling to currentFloor + devInitWindow - 1:', newCeiling);
      }


      if (notYetCount < devNotYetRequired) {
        // If we didn't find enough not-yet/blank, set ceiling to max
        newCeiling = maxOrderIndex;
      }
      
      // Ensure we don't exceed maximum order index
      newCeiling = Math.min(maxOrderIndex, newCeiling);
    }
    
    // Update if ceiling changed (floor already handled in devstate=2)
    if (newCeiling !== currentCeiling) {
      console.log('   Ceiling changed - Old:', currentCeiling, 'New:', newCeiling, '(floor remains:', currentFloor, ')');
      currentCeiling = newCeiling;
      updated = true;
    } else {
      console.log('   No ceiling changes needed - DevCeiling remains:', currentCeiling, '(floor:', currentFloor, ')');
    }
  }
  
  const result = {
    floor: currentFloor,
    ceiling: currentCeiling,
    devstate: currentDevState,
    updated,
    showProgress: false // Default no progress indicator for enhanced calculations
  };
  
  console.log('✅ calculateDevFloorCeiling: Completed calculation for domain:', domain);
  console.log('   Final result - Floor:', result.floor, 'Ceiling:', result.ceiling, 'DevState:', result.devstate, 'Updated:', result.updated, 'ShowProgress:', result.showProgress);
  console.log('   Milestone range size:', Math.max(0, result.ceiling - result.floor + 1), 'milestones');
  
  return result;
};

/**
 * Update profile with new floor and ceiling values for a domain
 * 
 * @param profile - The profile to update
 * @param domain - The domain to update
 * @param floor - New floor value
 * @param ceiling - New ceiling value
 * @param devstate - New devstate value (optional, defaults to 1)
 * @returns Updated profile
 */
export const updateProfileDevFloorCeiling = (
  profile: ChildProfile,
  domain: DomainKey,
  floor: number,
  ceiling: number,
  devstate: number = 1
): ChildProfile => {
  return {
    ...profile,
    devfloor: {
      ...profile.devfloor,
      [domain]: floor
    },
    devceiling: {
      ...profile.devceiling,
      [domain]: ceiling
    },
    devstate: {
      ...profile.devstate,
      [domain]: devstate
    },
    lastActiveDate: new Date().toISOString()
  };
};

/**
 * Get the milestone range (floor to ceiling) for a domain from a profile
 * 
 * @param profile - The child profile
 * @param domain - The development domain
 * @param domainMilestones - All milestones for the domain
 * @param storage - Storage interface for accessing AdminNodeDto data (optional)
 * @returns Object with calculated floor and ceiling, and array of milestones in range
 */
export const getMilestoneRange = async (
  profile: ChildProfile,
  domain: DomainKey,
  domainMilestones: MilestoneWithText[],
  storage?: StorageInterface
): Promise<{
  floor: number;
  ceiling: number;
  devstate: number;
  milestones: MilestoneWithText[];
  profileUpdated: boolean;
  showProgress?: boolean;
}> => {
  const result = await calculateDevFloorCeiling(profile, domain, domainMilestones, storage);
  
  // Get milestones in the floor-ceiling range using AdminNodeDto.Order if available
  let milestonesInRange: MilestoneWithText[] = [];
  
  if (storage) {
    const isPremium = appConfig.isPremium();
    const adminNodeName = mapDomainToAdminNodeName(domain, isPremium);
    
    try {
      const adminSegmentStorage = new AdminSegmentStorage(storage);
      const adminNode = await adminSegmentStorage.getAdminNodeByDomain(adminNodeName);
      
      if (adminNode && adminNode.Order && adminNode.Order.length > 0) {
        console.log('✅ Using AdminNodeDto.Order for milestone range filtering');
        
        // Create a map of MVarName to milestone data
        const milestoneByMVar = new Map<string, MilestoneWithText>();
        domainMilestones.forEach(milestone => {
          milestoneByMVar.set(milestone.mvar, milestone);
        });
        
        // Get milestones within the floor-ceiling range based on AdminNodeDto.Order
        for (let i = result.floor - 1; i < Math.min(result.ceiling, adminNode.Order.length); i++) {
          const orderItem = adminNode.Order[i];
          const milestone = milestoneByMVar.get(orderItem.Name);
          if (milestone) {
            milestonesInRange.push(milestone);
          }
        }
        
        console.log('   Found', milestonesInRange.length, 'milestones in range using AdminNodeDto.Order');
      } else {
        console.warn('⚠️ AdminNodeDto.Order not available for range filtering, using seqnum');
        milestonesInRange = domainMilestones.filter(
          milestone => milestone.seqnum >= result.floor && milestone.seqnum <= result.ceiling
        );
      }
    } catch (error) {
      console.error('❌ Error using AdminNodeDto.Order for range filtering:', error);
      milestonesInRange = domainMilestones.filter(
        milestone => milestone.seqnum >= result.floor && milestone.seqnum <= result.ceiling
      );
    }
  } else {
    console.warn('⚠️ No storage for range filtering, using seqnum');
    milestonesInRange = domainMilestones.filter(
      milestone => milestone.seqnum >= result.floor && milestone.seqnum <= result.ceiling
    );
  }
  
  return {
    floor: result.floor,
    ceiling: result.ceiling,
    devstate: result.devstate,
    milestones: milestonesInRange,
    profileUpdated: result.updated,
    showProgress: result.showProgress
  };
};

/**
 * Ensure a domain key is valid for the current app configuration
 * 
 * @param domain - Domain key to validate
 * @param isPremium - Whether the user has premium access
 * @returns Mapped domain key (e.g., 'fineMotor' -> 'motor' for standard users)
 */
export const mapDomainKey = (domain: string, isPremium: boolean): DomainKey => {
  // For standard users, map fine/gross motor to just 'motor'
  if (!isPremium) {
    if (domain === 'fineMotor' || domain === 'grossMotor') {
      return 'motor';
    }
  }
  
  // Handle case variations and normalize the domain key
  const normalizedDomain = domain.toLowerCase();
  
  // Map to proper DomainKey format
  switch (normalizedDomain) {
    case 'communication':
      return 'communication';
    case 'motor':
      return 'motor';
    case 'finemotor':
    case 'fine-motor':
      return 'finemotor';
    case 'grossmotor':
    case 'gross-motor':
      return 'grossmotor';
    case 'cognitive':
      return 'cognitive';
    case 'socialemotional':
    case 'social-emotional':
    case 'socioemotional':  // Handle possible variants
      return 'socialemotional';
    default:
      console.warn(`⚠️ Unknown domain key: ${domain}, defaulting to communication`);
      return 'communication';
  }
};

/**
 * Map domain key to AdminNodeDto.Name for proper AdminSegment lookup
 * 
 * @param domain - Domain key from profile
 * @param isPremium - Whether the user has premium access
 * @returns AdminNodeDto.Name string for AdminSegment lookup
 */
export const mapDomainToAdminNodeName = (domain: string, isPremium: boolean): string => {
  console.log(`🗺️ Mapping domain "${domain}" to AdminNodeDto.Name (Premium: ${isPremium})`);
  
  if (isPremium) {
    // Premium domain mappings to CDCX_* AdminNodeDto names
    switch (domain) {
      case 'communication':
        return 'CDCX_LC';
      case 'fineMotor':
      case 'finemotor':
        return 'CDCX_FM';
      case 'grossMotor':
      case 'grossmotor':
        return 'CDCX_GM';
      case 'cognitive':
        return 'CDCX_CG';
      case 'socialEmotional':
      case 'socialemotional':
      case 'social-emotional':
        return 'CDCX_SE';
      default:
        console.warn(`⚠️ Unknown premium domain: ${domain}, defaulting to CDCX_LC`);
        return 'CDCX_LC';
    }
  } else {
    // Standard domain mappings to CDC_* AdminNodeDto names
    switch (domain) {
      case 'communication':
        return 'CDC_LC';
      case 'motor':
      case 'fineMotor':
      case 'finemotor':
      case 'grossMotor':
      case 'grossmotor':
        return 'CDC_MT';
      case 'cognitive':
        return 'CDC_CG';
      case 'socialEmotional':
      case 'socialemotional':
      case 'social-emotional':
        return 'CDC_SE';
      default:
        console.warn(`⚠️ Unknown standard domain: ${domain}, defaulting to CDC_LC`);
        return 'CDC_LC';
    }
  }
};